import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom'
import {
  Avatar,
  Button,
  CssBaseline,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Input,
} from '@mui/material'
import LockIcon from '@mui/icons-material/LockOutlined'
import ROUTES from '../../routesConfig'
import anearApi from '../Api/AnearApi'

import UserAuthContainer from '../Containers/UserAuthContainer'
import useNavigation from '../Utils/navigationUtils'
import GoogleOauthButton from '../UI/GoogleOauthButton'
import { CONTENT_COLUMN_MAX_WIDTH, contentColumnPageShellSx } from '../UI/contentColumn'

const LoginForm = ({ error, setError, setUserArg, setPassword, handleSubmit, disabledSubmitButton }) => {

  return (
    <Box
      component="form"
      sx={{
        width: '100%', // Fix IE 11 issue.
        mt: 1, // marginTop shorthand
      }}
      noValidate
      autoComplete="off"
    >
      <FormControl
        required
        fullWidth
        error={error !== null}
        sx={{
          mb: 2,
          '& label + .MuiInputBase-root': {
            marginTop: 0
          }
        }}
        variant="standard"
      >
        <InputLabel
          htmlFor="user"
          shrink={true}
          sx={{
            color: '#06B6D4',
            fontWeight: 700,
            fontSize: '0.875rem',
            transform: 'none',
            position: 'static',
            mb: 0.25
          }}
        >
          User Name/Email
        </InputLabel>
        <Input
          id="user"
          name="user"
          autoComplete="username"
          required
          autoFocus
          sx={{
            color: '#E6E9EF',
            marginTop: '0 !important',
            '&::before': {
              borderBottom: '1px solid rgba(230,233,239,0.3)'
            },
            '&:hover:not(.Mui-disabled)::before': {
              borderBottom: '1px solid rgba(230,233,239,0.5)'
            },
            '&::after': {
              borderBottom: '2px solid #7C3AED'
            }
          }}
          onChange={e => { setUserArg(e.target.value); setError(null) }}
        />
      </FormControl>

      <FormControl
        required
        fullWidth
        error={error !== null}
        sx={{
          mb: 2,
          '& label + .MuiInputBase-root': {
            marginTop: 0
          }
        }}
        variant="standard"
      >
        <InputLabel
          htmlFor="password"
          shrink={true}
          sx={{
            color: '#06B6D4',
            fontWeight: 700,
            fontSize: '0.875rem',
            transform: 'none',
            position: 'static',
            mb: 0.25
          }}
        >
          Password
        </InputLabel>
        <Input
          name="password"
          type="password"
          id="password"
          autoComplete="current-password"
          required
          sx={{
            color: '#E6E9EF',
            marginTop: '0 !important',
            '&::before': {
              borderBottom: '1px solid rgba(230,233,239,0.3)'
            },
            '&:hover:not(.Mui-disabled)::before': {
              borderBottom: '1px solid rgba(230,233,239,0.5)'
            },
            '&::after': {
              borderBottom: '2px solid #7C3AED'
            }
          }}
          onChange={e => { setPassword(e.target.value); setError(null) }}
        />
      </FormControl>

      {/* Display error message in a box below the form fields */}
      {error && (
        <Box
          sx={{
            mt: 1,
            mb: 1,
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#EF4444',
              fontWeight: 500,
              lineHeight: 1.5
            }}
          >
            {error.message || 'An error occurred. Please try again.'}
          </Typography>
        </Box>
      )}

      <Button
        sx={{
          mt: 3,
          textTransform: 'none',
          fontWeight: 800,
          borderRadius: 999,
          px: 3,
          py: 1.5,
          fontSize: '1.1rem',
          background: 'linear-gradient(90deg,#7C3AED,#06B6D4)',
          boxShadow: '0 0 32px rgba(124,58,237,0.45)',
          '&:hover': {
            background: 'linear-gradient(90deg,#6D28D9,#0891B2)',
            boxShadow: '0 0 40px rgba(124,58,237,0.55)'
          },
          '&:disabled': {
            background: 'rgba(255,255,255,0.1)',
            color: 'rgba(230,233,239,0.5)'
          }
        }}
        type="submit"
        fullWidth
        variant="contained"
        disabled={disabledSubmitButton}
        onClick={handleSubmit}
      >
        Login
      </Button>
    </Box>
  )
}

const LoginPage = () => {
  const [ userArg, setUserArg ] = useState('')
  const [ password, setPassword ] = useState('')
  const [ disabledSubmitButton, setDisabledSubmitButton ] = useState(false)
  const [ redirectToReferrer, setRedirectToReferrer ] = useState(false)
  const [ error, setError ] = useState(null)

  const userAuthContainer = UserAuthContainer.useContainer()
  const { navigateToSignup, navigateToForgotPassword, navigateToGuestSignup, getNavigateComponent } = useNavigation()

  const location = useLocation()

  useEffect(() => {
    if (userAuthContainer.isGuestAuthed()) {
      userAuthContainer.clearUserAndToken()
    }
  }, [userAuthContainer])

  const clearLocalState = () => {
    setUserArg('')
    setPassword('')
    setDisabledSubmitButton(false)
    setRedirectToReferrer(false)
    setError(null)
  }

  const handleSubmit = async (e) => {

    e.preventDefault()

    try {
      setDisabledSubmitButton(true)
      const user = await anearApi.login(userArg, password)
      await loginSuccess(user)
    } catch(error) {
      loginFailure(error)
    }
  }

  const loginSuccess = async (user) => {
    clearLocalState()
    setRedirectToReferrer(true)
    await userAuthContainer.setUser(user)

    // Session payload omits role arrays; hydrate from /accounts so
    // privilege-aware UI can react immediately after login.
    try {
      await userAuthContainer.loadUser(true)
    } catch (error) {
      // Keep login successful even if account refresh fails.
    }
  }

  const loginFailure = error => {
    // flash a message in the login box and outline red
    setDisabledSubmitButton(false)
    setError(error)
  }

  let { from } = location.state || { from: { pathname: ROUTES.HOME } }

  const guestButtonHandler = e => {
    e.preventDefault()
    clearLocalState()
    navigateToGuestSignup(from, {
      source: 'login',
      appIconUrl: from?.state?.playableApp?.iconUrl || null,
      appName: from?.state?.playableApp?.shortName || null,
      eventName: from?.state?.eventName || null
    })
  }

  const signupButtonHander = () => {
    clearLocalState()
    navigateToSignup(from)
  }

  const forgotPasswordHandler = () => {
    clearLocalState()
    navigateToForgotPassword()
  }

  const oauthReturnTarget = `${from?.pathname || ROUTES.HOME}${from?.search || ''}${from?.hash || ''}`

  if (redirectToReferrer || userAuthContainer.isRegisteredAuthed()) return getNavigateComponent(from)

  const shouldShowBeOurGuestButton = !userAuthContainer.isAuthed()
  const shouldShowSignupButton = true

  return (
    <Box sx={contentColumnPageShellSx}>
      <CssBaseline />
      <Paper
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          px: 3,
          pb: 3,
          bgcolor: '#0B0F19',
          color: '#E6E9EF',
          borderRadius: 3,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: CONTENT_COLUMN_MAX_WIDTH
        }}
      >
        <Avatar
          sx={{
            m: 1,
            backgroundColor: 'rgba(124, 58, 237, 0.3)',
            color: '#A78BFA'
          }}
        >
          <LockIcon />
        </Avatar>
        <Typography
          component="h1"
          variant="h5"
          sx={{
            color: '#E6E9EF',
            fontWeight: 800,
            letterSpacing: 0.2
          }}
        >
          Anear Login
        </Typography>

        <LoginForm
          error={error}
          setError={setError}
          setUserArg={setUserArg}
          setPassword={setPassword}
          handleSubmit={handleSubmit}
          disabledSubmitButton={disabledSubmitButton}
        />

        <GoogleOauthButton
          label="Continue with Google"
          returnTarget={oauthReturnTarget}
        />

        <Typography
          variant="body2"
          sx={{
            mt: 1,
            color: 'rgba(230,233,239,0.62)',
            textAlign: 'center'
          }}
        >
          Use your Google account to sign in without a password.
        </Typography>

        {shouldShowBeOurGuestButton && (
          <Button
            sx={{
              mt: 1,
              color: '#A78BFA',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'rgba(167,139,250,0.1)'
              }
            }}
            size="small"
            onClick={guestButtonHandler}
          >
            Continue As Guest
          </Button>
        )}
        {shouldShowSignupButton && (
          <Button
            sx={{
              mt: 1,
              color: '#A78BFA',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'rgba(167,139,250,0.1)'
              }
            }}
            size="small"
            onClick={signupButtonHander}
          >
            Signup
          </Button>
        )}
        <Button
          sx={{
            mt: 1,
            color: 'rgba(230,233,239,0.78)',
            textTransform: 'none',
            fontSize: '0.95rem',
            fontWeight: 500,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.06)'
            }
          }}
          size="small"
          onClick={forgotPasswordHandler}
        >
          Forgot password?
        </Button>
      </Paper>
    </Box>
  )
}

export default LoginPage
